/*---------------------------------------------------------------------------------------------
 * SMART STICKY NAV
 * Applies classes dynamically, based on scroll direction
 *--------------------------------------------------------------------------------------------*/

import throttle from 'lodash/throttle';
import * as React from 'react';

import { action, direction, ISmartStickyNavProps } from './smart-sticky-nav.props.autogenerated';

// ==============================================================================
// CLASS DEFINITION
// ==============================================================================
/**
 *
 * SmartStickyNav component
 * @extends {React.PureComponent<ISmartStickyNavProps<{}>>}
 */
class SmartStickyNav extends React.PureComponent<ISmartStickyNavProps<{}>> {
    // Throttled event handler
    private _throttledScrollhandler: () => void;
    private _lastKnownScrollPosition:number = 0;

    // --------------------------------------------------------------
    // Constructor
    // --------------------------------------------------------------
    constructor(props: ISmartStickyNavProps<{}>) {
        super(props);

        // Throttle the event handler to prevent event overloading
        this._throttledScrollhandler = throttle(this._scrollHandler.bind(this), 50);

    }

    // --------------------------------------------------------------
    // Attach event handler
    // --------------------------------------------------------------
    public componentDidMount(): void {
        if (process.env.CURRENT_ENVIRONMENT === 'web') {
            window && window.addEventListener && window.addEventListener('scroll', this._throttledScrollhandler);
        }
    }

    // --------------------------------------------------------------
    // Detach event handler
    // --------------------------------------------------------------
    public componentWillUnmount(): void {
        window && window.removeEventListener && window.removeEventListener('scroll', this._throttledScrollhandler);
    }

    // --------------------------------------------------------------
    // Required, but doesn't do anything
    // --------------------------------------------------------------
    public render(): null {
        return null;
    }

    // --------------------------------------------------------------
    // Process the action
    // --------------------------------------------------------------
    private _performAction(targetElement:HTMLCollectionOf<Element>, actionFunction:'add' | 'remove'): void {
        const { actionClass } = this.props.config;
        // Peform the action
        for (let index = 0; index < targetElement.length; index++) {
            targetElement[index].classList[actionFunction](actionClass);
        }
    }

    private _scrollHandler(): void {
        const { targetClass, bidirectional, direction:scrollDirection, action:selectedAction } = this.props.config;

        // Get the current scroll position
        const currentPosition = window.scrollY;

        // Get the target element on DOM
        const targetElement = document.getElementsByClassName(targetClass);

        // Determine the appropriate function
        const actionFunction = (selectedAction === action.add) ? 'add' : 'remove';

        // Determine the appropriate function used when biderectional is true
        const bidirectionalActionFunction = (selectedAction === action.add) ? 'remove' : 'add';

        if (currentPosition > this._lastKnownScrollPosition) {
            // The user is Scrolling DOWN
            if (scrollDirection === direction.down) {
                this._performAction(targetElement, actionFunction);
            }

            if (bidirectional && scrollDirection === direction.up) {
                this._performAction(targetElement, bidirectionalActionFunction);
            }
        } else {
            // The user is Scrolling UP
            if (scrollDirection === direction.up) {
                this._performAction(targetElement, actionFunction);
            }

            if (bidirectional && scrollDirection === direction.down) {
                this._performAction(targetElement, bidirectionalActionFunction);
            }
        }

        this._lastKnownScrollPosition = currentPosition <= 0 ? 0 : currentPosition;
    }
}

export default SmartStickyNav;
